/**
 * DadosGeraisTab - Tab showing cliente general information
 *
 * Displays all client fields organized in logical sections:
 * - Identificação (código, razão social, nome fantasia, CNPJ, status)
 * - Localização (endereço completo)
 * - Contato (telefone, email)
 * - Configurações de Vencimento (período segregação, sinalizações)
 * - Configurações do Sistema (lotes, auditiva, beebagem)
 * - Configurações de Agendamento (Epic 46 - intervalo min/max entre contagens)
 * - Configuração SMTP (servidor, porta, TLS, emails)
 * - Datas (criação, atualização)
 */
import React, { useState, useRef, useCallback } from 'react';
import { CheckCircle, Building2, MapPin, Phone, Calendar, Settings, Mail, Clock, CalendarClock, Upload, Trash2, Image, Loader2 } from 'lucide-react';
import type { Cliente } from '../../services/clienteService';
import clienteService from '../../services/clienteService';
import apiClient from '../../services/apiClient';
import { mascaraTelefone } from '../../lib/utils';
import { msg } from '../../utils/messages';

interface DadosGeraisTabProps {
  cliente: Cliente;
  onClienteUpdate?: (updated: Cliente) => void;
}

// Componente auxiliar para campos somente leitura
const ReadOnlyField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">
      {label}
    </label>
    <div className="text-sm text-gray-900 dark:text-white">
      {value || '-'}
    </div>
  </div>
);

// Componente auxiliar para checkbox somente leitura
const ReadOnlyCheckbox: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
  <div className="flex items-center gap-2">
    {checked ? (
      <CheckCircle className="w-4 h-4 text-green-600" />
    ) : (
      <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
    )}
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </div>
);

/**
 * Valor em dias vindo do cadastro.
 *
 * ⛔ N120: NUNCA usar `|| 90` (nem `?? 90`) para exibir estes campos. Em JS
 * `0 || 90` é 90, então um cliente cadastrado com 0 aparecia como "90 dias" —
 * a tela de consulta mentia sobre o próprio cadastro, e foi por aí que o caso
 * da Alana (contagem 1498) passou sem ninguém ver. `0` é valor válido e é
 * exibido como 0; só `null`/`undefined` é "nunca configurado", e aí dizemos
 * isso explicitamente em vez de inventar um número.
 */
const Dias: React.FC<{ value?: number | null; vazio?: string }> = ({ value, vazio = 'não configurado' }) =>
  value == null ? (
    <span className="text-gray-400 dark:text-gray-500 italic">{vazio}</span>
  ) : (
    <>{value} dias</>
  );

// Componente auxiliar para seção
const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  children: React.ReactNode
}> = ({ icon, title, color, children }) => (
  <div className="space-y-3">
    <div className={`flex items-center gap-1.5 pb-1.5 border-b ${color}`}>
      {icon}
      <span className="text-xs font-semibold uppercase tracking-wide">
        {title}
      </span>
    </div>
    <div className="space-y-2.5">
      {children}
    </div>
  </div>
);

export const DadosGeraisTab: React.FC<DadosGeraisTabProps> = ({ cliente, onClienteUpdate }) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(
    cliente.logo_proxy_url ? `${cliente.logo_proxy_url}?t=${Date.now()}` : null
  );
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setLogoUrl(cliente.logo_proxy_url ? `${cliente.logo_proxy_url}?t=${Date.now()}` : null);
  }, [cliente.id, cliente.logo_proxy_url]);

  const handleLogoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      msg.warning('Formato inválido. Use PNG, JPG ou SVG.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      msg.warning('Arquivo excede 2MB.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post(`/clientes/${cliente.id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(`${res.data.logo_proxy_url}?t=${Date.now()}`);
      msg.success('Logo atualizado com sucesso');
      if (onClienteUpdate) onClienteUpdate({ ...cliente, has_logo: true, logo_proxy_url: res.data.logo_proxy_url });
    } catch (error: any) {
      msg.error(error.response?.data?.message || 'Erro ao fazer upload do logo');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [cliente, onClienteUpdate]);

  const handleLogoDelete = useCallback(async () => {
    if (!confirm('Remover logo do cliente?')) return;
    try {
      await apiClient.delete(`/clientes/${cliente.id}/logo`);
      setLogoUrl(null);
      msg.success('Logo removido');
      if (onClienteUpdate) onClienteUpdate({ ...cliente, has_logo: false, logo_proxy_url: undefined });
    } catch (error: any) {
      msg.error(error.response?.data?.message || 'Erro ao remover logo');
    }
  }, [cliente, onClienteUpdate]);

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        Informações Cadastrais
      </h3>

      {/* Logo do Cliente */}
      <div className="mb-6">
        <Section
          icon={<Image className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />}
          title="Logo do Cliente"
          color="border-teal-200 dark:border-teal-800 text-teal-600 dark:text-teal-400"
        >
          <div className="flex items-center gap-4">
            <div className="w-[120px] h-[120px] flex-shrink-0 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800 overflow-hidden">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo do cliente"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <Image className="w-10 h-10 text-gray-300 dark:text-gray-600" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 rounded-md transition-colors"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isUploading ? 'Enviando...' : 'Upload Logo'}
              </button>
              {logoUrl && (
                <button
                  onClick={handleLogoDelete}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 rounded-md transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Remover
                </button>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">PNG, JPG ou SVG. Máx 2MB.</p>
            </div>
          </div>
        </Section>
      </div>

      {/* Grid de 4 colunas */}
      <div className="grid grid-cols-4 gap-5">

        {/* COLUNA 1: IDENTIFICAÇÃO */}
        <Section
          icon={<Building2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />}
          title="Identificação"
          color="border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400"
        >
          <ReadOnlyField label="Código" value={cliente.codigo} />
          <ReadOnlyField label="Razão Social" value={cliente.razao_social} />
          <ReadOnlyField label="Nome Fantasia" value={cliente.nome_fantasia} />
          <ReadOnlyField
            label="CNPJ"
            value={cliente.cnpj ? clienteService.formatCNPJ(cliente.cnpj) : '-'}
          />
          <ReadOnlyField
            label="Status"
            value={
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                cliente.ativo
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {cliente.ativo ? 'Ativo' : 'Inativo'}
              </span>
            }
          />
          <div className="pt-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Regime Tributário
            </label>
            <div className="flex flex-col gap-1">
              <ReadOnlyCheckbox label="Com Regime" checked={cliente.com_regime} />
              <ReadOnlyCheckbox label="Sem Regime" checked={cliente.sem_regime} />
            </div>
          </div>
        </Section>

        {/* COLUNA 2: LOCALIZAÇÃO + CONTATO */}
        <div className="space-y-5">
          <Section
            icon={<MapPin className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />}
            title="Localização"
            color="border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400"
          >
            <ReadOnlyField label="CEP" value={cliente.cep} />
            <ReadOnlyField label="Endereço" value={cliente.endereco} />
            <ReadOnlyField label="Bairro" value={cliente.bairro} />
            <ReadOnlyField
              label="Cidade/Estado"
              value={cliente.cidade && cliente.estado
                ? `${cliente.cidade}/${cliente.estado}`
                : cliente.cidade || cliente.estado || '-'
              }
            />
          </Section>

          <Section
            icon={<Phone className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />}
            title="Contato"
            color="border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400"
          >
            <ReadOnlyField
              label="Telefone"
              value={cliente.telefone ? mascaraTelefone(cliente.telefone) : '-'}
            />
            <ReadOnlyField label="Email" value={cliente.email} />
          </Section>
        </div>

        {/* COLUNA 3: CONFIGURAÇÕES DE VENCIMENTO */}
        <Section
          icon={<Clock className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />}
          title="Config. Vencimento"
          color="border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400"
        >
          {/* SEGREGA automaticamente — retira do saldo vendável */}
          <div className="bg-red-50 dark:bg-red-900/20 rounded-md p-2 border border-red-200 dark:border-red-800">
            <label className="block text-xs font-semibold text-red-700 dark:text-red-400 mb-0.5">
              Período de Segregação (dias)
            </label>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              <Dias value={cliente.periodo_segregacao} />
            </div>
            <p className="text-[10px] text-red-700 dark:text-red-400 mt-1 leading-tight">
              ⚠️ SEGREGA automaticamente: produtos com validade ≤ este valor (dias)
              são retirados do saldo vendável.
            </p>
          </div>

          {/* Alertas — apenas visual/sonoro, NÃO segregam */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-md p-2 space-y-1.5">
            <span className="block text-xs font-semibold text-amber-700 dark:text-amber-300 leading-tight">
              Alertas de Vencimento (dias) — apenas visual/sonoro, NÃO segrega
            </span>
            <div className="grid grid-cols-3 gap-1 text-center">
              {([
                ['Nível 1', cliente.sinalizar_vencimento_1],
                ['Nível 2', cliente.sinalizar_vencimento_2],
                ['Nível 3', cliente.sinalizar_vencimento_3],
              ] as const).map(([label, valor]) => (
                <div key={label}>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {valor == null ? (
                      <span className="text-gray-400 dark:text-gray-500 italic text-xs">—</span>
                    ) : (
                      `${valor}d`
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ReadOnlyField
            label="Override Mín (opcional)"
            value={<Dias value={cliente.intervalo_vencimento_min_dias} vazio="usa Nível 1" />}
          />
          <ReadOnlyField
            label="Override Máx (opcional)"
            value={<Dias value={cliente.intervalo_vencimento_max_dias} vazio="usa Nível 2" />}
          />
          <p className="text-[10px] text-gray-500 dark:text-gray-400 italic leading-tight">
            Por padrão, o sinal auditivo dispara entre <strong>Nível 1</strong> e{' '}
            <strong>Nível 2</strong>. Os campos Override são opcionais e substituem esse range.
          </p>

          {/* Notificações — mesma seção do cadastro */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 space-y-1.5">
            <span className="block text-xs font-semibold text-gray-600 dark:text-gray-400">
              Notificações
            </span>
            <ReadOnlyCheckbox
              label="Sinalização auditiva entre Nível 1 e Nível 2"
              checked={cliente.sinalizacao_auditiva_ativa ?? false}
            />
            <p className="text-[10px] text-gray-500 dark:text-gray-400 italic leading-tight">
              🔔 Toca som + exibe a mensagem no console do scanner durante a leitura.
              Item é CONTADO NORMALMENTE (não é segregado, entra no saldo vendável).
            </p>
            <ReadOnlyField
              label="Mensagem exibida no console do scanner"
              value={cliente.mensagem_log_beebagem ? (
                <span className="text-xs italic text-gray-600 dark:text-gray-400">
                  "{cliente.mensagem_log_beebagem}"
                </span>
              ) : '-'}
            />
          </div>
        </Section>

        {/* COLUNA 4: CONFIGURAÇÕES DO SISTEMA + SMTP + DATAS */}
        <div className="space-y-5">
          <Section
            icon={<Settings className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />}
            title="Config. Sistema"
            color="border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400"
          >
            <ReadOnlyCheckbox
              label="Input Saldos via Lotes"
              checked={cliente.input_saldos_sistema_via_lotes ?? false}
            />
            {/* Sinalização auditiva + mensagem ficam na seção Notificações
                (Config. Vencimento), na mesma ordem do cadastro. */}
          </Section>

          {/* Epic 46: Configurações de Intervalo de Agendamento */}
          <Section
            icon={<CalendarClock className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />}
            title="Config. Agendamento"
            color="border-cyan-200 dark:border-cyan-800 text-cyan-600 dark:text-cyan-400"
          >
            <ReadOnlyCheckbox
              label="Controle de intervalo mínimo"
              checked={cliente.intervalo_agendamento_min_ativo ?? false}
            />
            <ReadOnlyField
              label="Mín."
              value={<Dias value={cliente.intervalo_agendamento_min_dias} vazio="-" />}
            />
            <ReadOnlyCheckbox
              label="Controle de intervalo máximo"
              checked={cliente.intervalo_agendamento_max_ativo ?? false}
            />
            <ReadOnlyField
              label="Máx."
              value={<Dias value={cliente.intervalo_agendamento_max_dias} vazio="-" />}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 italic">
              Controla intervalo entre contagens agendadas
            </div>
          </Section>

          <Section
            icon={<Mail className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
            title="Config. SMTP"
            color="border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400"
          >
            <ReadOnlyField label="Servidor SMTP" value={cliente.smtp_servidor} />
            <ReadOnlyField
              label="Porta / TLS"
              value={
                <span>
                  {cliente.smtp_porta ?? 587}
                  {' / '}
                  <span className={cliente.smtp_tls ? 'text-green-600' : 'text-gray-400'}>
                    {cliente.smtp_tls ? 'Ativo' : 'Inativo'}
                  </span>
                </span>
              }
            />
            <ReadOnlyField
              label="Emails de Cópia"
              value={cliente.email_copia ? (
                <span className="text-xs break-all">{cliente.email_copia}</span>
              ) : '-'}
            />
          </Section>

          <Section
            icon={<Calendar className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />}
            title="Datas"
            color="border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400"
          >
            <ReadOnlyField
              label="Cadastrado em"
              value={cliente.created_at
                ? new Date(cliente.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : '-'
              }
            />
            <ReadOnlyField
              label="Atualizado em"
              value={cliente.updated_at
                ? new Date(cliente.updated_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : '-'
              }
            />
          </Section>
        </div>
      </div>
    </div>
  );
};
